import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { logger } from '@/utils/logger/logger';
import {
  appendCheckpoint,
  cancelTracking,
  finishTracking,
  getActiveTrackings,
  TrackingKey,
} from '@heidi/runtime/performance';

const RECORDING_SESSION_ID_METADATA = 'recording_session_id';
const RECORDING_ATTEMPT_KEYS = [
  TrackingKey.SCRIBE_START_RECORDING,
  TrackingKey.SCRIBE_RESUME_RECORDING,
] as const;

export type RecordingAttemptTrackingKey = (typeof RECORDING_ATTEMPT_KEYS)[number];

export type RecordingAttemptTracking = {
  trackingKey: RecordingAttemptTrackingKey;
  trackingInstanceId?: number | null;
};

export type RecordingStartOptions = {
  shouldBypassConsentCheck?: boolean;
  tracking?: RecordingAttemptTracking;
};

export const recordingStartTrackingMetadata = (
  sessionId: string
): Record<string, unknown> => ({
  [RECORDING_SESSION_ID_METADATA]: sessionId,
});

const isTrackingOwnedBySession = (
  key: RecordingAttemptTrackingKey,
  sessionId: string
): boolean =>
  getActiveTrackings().get(key)?.metadata?.[RECORDING_SESSION_ID_METADATA] === sessionId;

/** The attempt this session already opened, start or resume. */
export const recordingAttemptOwnedBySession = (
  sessionId: string
): RecordingAttemptTracking | undefined => {
  for (const trackingKey of RECORDING_ATTEMPT_KEYS) {
    const entry = getActiveTrackings().get(trackingKey);
    if (entry?.metadata?.[RECORDING_SESSION_ID_METADATA] !== sessionId) continue;
    return { trackingKey, trackingInstanceId: entry.instanceId };
  }
  return undefined;
};

export const ownsRecordingAttempt = ({
  trackingKey,
  trackingInstanceId,
}: RecordingAttemptTracking): boolean =>
  trackingInstanceId === undefined ||
  (trackingInstanceId !== null &&
    getActiveTrackings().get(trackingKey)?.instanceId === trackingInstanceId);

export const appendRecordingAttemptCheckpoint = (
  tracking: RecordingAttemptTracking,
  checkpointName: string,
  metadata?: Record<string, unknown>
): void => {
  if (!ownsRecordingAttempt(tracking)) {
    // A retry replaced this instance. The old callback still reaches dispatch.
    // Log that, and leave the capture path unchanged.
    if (
      checkpointName === 'mutation_dispatched' &&
      typeof tracking.trackingInstanceId === 'number'
    ) {
      logger.warn('[recording-start] superseded attempt continued after retry');
    }
    return;
  }
  appendCheckpoint({ key: tracking.trackingKey, checkpointName, metadata });
};

export const finishRecordingAttempt = (
  tracking: RecordingAttemptTracking,
  params: {
    outcome?: 'failed';
    error?: unknown;
    metadata?: Record<string, unknown>;
  } = {}
): void => {
  if (!ownsRecordingAttempt(tracking)) return;
  if (params.outcome === 'failed') {
    finishTracking({
      key: tracking.trackingKey,
      outcome: 'failed',
      error: params.error,
      metadata: params.metadata,
    });
    return;
  }
  finishTracking({ key: tracking.trackingKey, metadata: params.metadata });
};

export const cancelRecordingAttempt = (tracking: RecordingAttemptTracking): void => {
  if (!ownsRecordingAttempt(tracking)) return;
  cancelTracking({ key: tracking.trackingKey });
};

export const cancelRecordingStartTrackingForSession = ({
  sessionId,
  gate,
  reason,
}: {
  sessionId: string;
  gate: 'consent' | 'shadow_heidi';
  reason: string;
}): boolean => {
  const ownedKeys = RECORDING_ATTEMPT_KEYS.filter((key) =>
    isTrackingOwnedBySession(key, sessionId)
  );

  for (const key of ownedKeys) {
    finishTracking({
      key,
      outcome: 'failed',
      error: createSessionFlowPreflightError('StartRecordingCancelled', reason),
      metadata: { preflight_gate: gate },
    });
  }
  return ownedKeys.length > 0;
};
